import { banReasonSchema, type GuildBan } from '@discordo/shared';
import { useMemo, useState, type ReactElement } from 'react';

import type { GuildMemberSummary } from '../store/channels';
import { BansDialog } from './BansDialog';
import { ConfirmDialog, PromptDialog } from './ChannelDialogs';

/**
 * Right column: who belongs to the guild, split by who is connected.
 *
 * Presence arrives from the gateway as changes, so the set handed in here is
 * the store's running tally, not a snapshot the API returns.
 *
 * Moderation is handed in whole or not at all. Which people may be acted upon
 * is decided by whoever builds it, from the same rank rule the API enforces —
 * hiding the menu is what keeps anybody from being invited into a refusal, and
 * it is never what makes the refusal happen.
 */

export interface MemberModeration {
  /** Whether the signed-in person may act upon this member. */
  canModerate: (member: GuildMemberSummary) => boolean;
  kick: (userId: string) => Promise<void>;
  /** The reason is optional; blank means none was given. */
  ban: (userId: string, reason?: string) => Promise<void>;
  listBans: () => Promise<GuildBan[]>;
  revokeBan: (userId: string) => Promise<void>;
}

export interface MemberListProps {
  members: GuildMemberSummary[];
  onlineUserIds: ReadonlySet<string>;
  /** Moderation actions, or `null` for somebody who administers nothing. */
  moderation?: MemberModeration | null;
}

type DialogState =
  | { kind: 'kick'; member: GuildMemberSummary }
  | { kind: 'ban'; member: GuildMemberSummary }
  | { kind: 'bans' };

function nameOf(member: GuildMemberSummary): string {
  return member.nickname ?? member.user.displayName;
}

interface MemberRowProps {
  member: GuildMemberSummary;
  online: boolean;
  /** The ⋯ menu, or `null` when this person cannot be acted upon. */
  menu: {
    open: boolean;
    onToggle: () => void;
    onKick: () => void;
    onBan: () => void;
  } | null;
}

function MemberRow({ member, online, menu }: MemberRowProps): ReactElement {
  return (
    <li className={online ? 'member-row' : 'member-row is-offline'}>
      <span className="avatar" aria-hidden="true">
        {nameOf(member).slice(0, 1).toUpperCase()}
      </span>
      <span className="member-name">{nameOf(member)}</span>
      {member.role !== 'member' && (
        <span className="member-role">{member.role === 'owner' ? 'dono' : 'admin'}</span>
      )}

      {menu !== null && (
        <div className="sidebar-menu member-menu">
          <button
            type="button"
            className="sidebar-menu-toggle"
            aria-label={`Opções de ${nameOf(member)}`}
            aria-expanded={menu.open}
            onClick={menu.onToggle}
          >
            <span aria-hidden="true">⋯</span>
          </button>

          {menu.open && (
            <ul className="sidebar-menu-list">
              <li>
                <button type="button" onClick={menu.onKick}>
                  Expulsar
                </button>
              </li>
              <li>
                <button type="button" className="is-destructive" onClick={menu.onBan}>
                  Banir
                </button>
              </li>
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

export function MemberList({
  members,
  onlineUserIds,
  moderation = null,
}: MemberListProps): ReactElement {
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const { online, offline } = useMemo(() => {
    const connected: GuildMemberSummary[] = [];
    const away: GuildMemberSummary[] = [];

    for (const member of members) {
      if (onlineUserIds.has(member.user.id)) {
        connected.push(member);
      } else {
        away.push(member);
      }
    }

    return { online: connected, offline: away };
  }, [members, onlineUserIds]);

  function renderRow(member: GuildMemberSummary, isOnline: boolean): ReactElement {
    const actionable = moderation !== null && moderation.canModerate(member);

    return (
      <MemberRow
        key={member.user.id}
        member={member}
        online={isOnline}
        menu={
          actionable
            ? {
                open: openMenuId === member.user.id,
                onToggle: () => {
                  setOpenMenuId((current) =>
                    current === member.user.id ? null : member.user.id,
                  );
                },
                onKick: () => {
                  setOpenMenuId(null);
                  setDialog({ kind: 'kick', member });
                },
                onBan: () => {
                  setOpenMenuId(null);
                  setDialog({ kind: 'ban', member });
                },
              }
            : null
        }
      />
    );
  }

  return (
    <aside className="members" aria-label="Membros">
      {moderation !== null && (
        <button
          type="button"
          className="members-bans"
          onClick={() => {
            setDialog({ kind: 'bans' });
          }}
        >
          Banidos
        </button>
      )}

      <section className="members-section">
        <h2 className="members-title">Online — {online.length}</h2>
        <ul className="members-list">{online.map((member) => renderRow(member, true))}</ul>
      </section>

      <section className="members-section">
        <h2 className="members-title">Offline — {offline.length}</h2>
        <ul className="members-list">{offline.map((member) => renderRow(member, false))}</ul>
      </section>

      {moderation !== null && dialog?.kind === 'kick' && (
        <ConfirmDialog
          title={`Expulsar ${nameOf(dialog.member)}`}
          message="A pessoa sai do servidor agora. Ela pode voltar se receber um convite novo — para fechar a porta, use banir."
          confirmLabel="Expulsar do servidor"
          submittingLabel="Expulsando…"
          onConfirm={() => moderation.kick(dialog.member.user.id)}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}

      {moderation !== null && dialog?.kind === 'ban' && (
        <PromptDialog
          title={`Banir ${nameOf(dialog.member)}`}
          hint="A pessoa sai do servidor e não entra de novo, nem com um convite novo. O banimento pode ser revogado depois, em Banidos."
          label="Motivo (opcional)"
          initialValue=""
          submitLabel="Banir do servidor"
          submittingLabel="Banindo…"
          schema={banReasonSchema}
          onSubmit={(reason) =>
            moderation.ban(dialog.member.user.id, reason === '' ? undefined : reason)
          }
          onClose={() => {
            setDialog(null);
          }}
        />
      )}

      {moderation !== null && dialog?.kind === 'bans' && (
        <BansDialog
          listBans={moderation.listBans}
          revokeBan={moderation.revokeBan}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
    </aside>
  );
}

export default MemberList;
